import React from 'react';
import { ActivityInterface, StepInterface } from 'pages/Activities/activities.context';
import { Tag } from 'components/Tag/Tag';
import { themeOptions } from 'theme';
import { ActivityAssignment } from '__generated__/graphql';
import { Store } from '@restore/services-lib';

export const createUpdateActivityFromActivity = (activity: ActivityInterface) => ({
  id: activity.id,
  campaignId: activity.campaign?.id,
  assignedStoreIds: activity.assignedStores.map((store: Store) => store.id),
  subject: activity.subject,
  description: activity.description,
  startDate: activity.startDate,
  dueDate: activity.dueDate,
  tagIds: activity.tags.map((tag) => tag.id),
  assignedUserIds: activity.assignedUsers.map((user) => user.id),
  ccRecipientIds: activity.ccRecipients.map((rec) => rec.id),
  steps: activity.steps.map((step) => ({
    id: step.id,
    subject: step.subject,
    description: step.description,
    startDate: step.startDate,
    dueDate: step.dueDate,
    estimatedTimeToComplete: step.estimatedTimeToComplete,
    requiresReview: step.requiresReview,
    isRequired: step.isRequired,
    assignedUserId: step.assignedUser?.id,
    media: step.media,
    removedFileIds: [],
    sortOrder: step.sortOrder,
    question: step.question
      ? {
          id: step.question.id,
          questionDetail: step.question.questionDetail,
          type: step.question.type,
          options: step.question.options,
          isMultiple: step.question.isMultiple,
          questionChoices: step.question.questionChoices?.map((questionChoice) => ({
            id: questionChoice.id,
            label: questionChoice.label,
            pillarScore: questionChoice.pillarScore,
            question: questionChoice.question,
            sortOrder: questionChoice.sortOrder,
            value: questionChoice.value,
          })),
        }
      : undefined,
  })),
  state: activity.state,
});

const colors = themeOptions.colors;

export const getActivityStatus = (activity: ActivityInterface): JSX.Element => {
  if (activity.state === 'Cancelled') {
    return <Tag text={activity.state} color={colors.neutral600} />;
  }

  if (activity.state === 'Draft') {
    return <Tag text={activity.state} color={colors.orange} />;
  }

  if (activity.state === 'Complete') {
    return <Tag text='Complete' color={colors.success900} />;
  }

  if (activity.dueDate && String(activity.dueDate) < new Date().toISOString().slice(0, 10)) {
    return <Tag text='Overdue' color={colors.error900} />;
  }

  // status is Active if it reaches this line
  return <Tag text={activity.state} color={colors.info900} />;
};

type AssignmentWithStatus = Pick<ActivityAssignment, 'state' | 'completedDate'> & {
  activity: Pick<ActivityAssignment['activity'], 'dueDate'>;
};
export const getActivityAssignmentStatus = (assignment: AssignmentWithStatus): JSX.Element => {
  const colors = themeOptions.colors;

  if (assignment.state === 'Cancelled') {
    return <Tag text={assignment.state} color={colors.neutral600} />;
  }

  if (assignment.state === 'Draft') {
    return <Tag text={assignment.state} color={colors.orange} />;
  }

  if (assignment.completedDate) {
    return <Tag text='Complete' color={colors.success900} />;
  }

  if (
    assignment.activity.dueDate &&
    String(assignment.activity.dueDate) < new Date().toISOString().slice(0, 10)
  ) {
    return <Tag text='Overdue' color={colors.error900} />;
  }

  // status is Active if it reaches this line
  return <Tag text={assignment.state} color={colors.info900} />;
};

export const getStepStatus = (step: StepInterface): JSX.Element => {
  if (step.activity.state === 'Cancelled') {
    return <Tag text='Cancelled' color={colors.neutral600} />;
  }

  if (step.activity.state === 'Draft') {
    return <Tag text='Draft' color={colors.orange} />;
  }

  if (step.completedDate) {
    return <Tag text='Complete' color={colors.success900} />;
  }

  if (step.requiresReview) {
    return <Tag text='Review Required' color={colors.warning} />;
  }

  if (step.dueDate && String(step.dueDate) < new Date().toISOString().slice(0, 10)) {
    return <Tag text='Overdue' color={colors.error900} />;
  }

  // status is Active if it reaches this line
  return <Tag text='Active' color={colors.info900} />;
};

export const forceFocus = (input: { focus: () => void }) => {
  if (input) {
    setTimeout(() => {
      input.focus();
    }, 10);
  }
};
